import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, Image, Heading } from 'rebass';
import { useWindowSize } from 'react-use';
import { FiX, FiChevronLeft, FiChevronRight, FiCheckCircle } from 'react-icons/fi';

import EditChildJournal from 'collections/edit/child-journal';
import { FT_COLORS } from './theme';
import { PrimaryButton } from './components/FtButton';
import bookImage from './assets/TFT-FTUE.png';
import giraffe from './assets/Giraffe.png';

export const FTUE_STORAGE_KEY = 'talkingFamilyTreeFtueSeen';

const STEPS = { WELCOME: 0, PROFILE: 1, SUCCESS: 2 };

// First-time-user experience for the Talking Family Tree.
//  1. Welcome
//  2. "Before You Begin" — reuses collections/edit/child-journal (baby profile);
//     its save → onSuccess advances to step 3.
//  3. "Great! Next, create an invitation…" success.
// Shown as a modal on desktop, full-screen on mobile.
const Ftue = ({ onClose, onCreateInvitation }) => {
  const { width } = useWindowSize();
  const isDesktop = width >= 768;
  const [step, setStep] = useState(STEPS.WELCOME);
  const profileRef = useRef(null);

  // Submit the reused child-journal form from our footer "Next".
  const submitProfile = () => {
    const btn = profileRef.current && profileRef.current.querySelector('button[type="submit"]');
    if (btn) btn.click(); // no-op while invalid (button stays disabled) — mirrors a disabled Next
  };

  const heading = (text, p) => (
    <Heading p={p} px={[3, 4]} fontFamily="Bitter" fontWeight={500} fontSize={"30"} color={"#000"} textAlign="center">
      {text}
    </Heading>
  );
  const sub = (text, p) => (
    <Text p={p} px={[3, 4]} fontFamily="Poppins" fontSize="16px" color="#64748B" textAlign="center" mt={2}>
      {text}
    </Text>
  );

  let content;
  if (step === STEPS.WELCOME) {
    content = (
      <Flex flexDirection="column">
        {heading('Welcome to Talking Family Tree!', 0)}
        {sub('Invite family to share who they are and record heartfelt video messages that will become part of your book forever.', 0)}
        <Image src={bookImage} maxWidth={400} m="auto" alt="Talking Family Tree book" width="100%" mt={4} sx={{ borderRadius: '8px' }} />
      </Flex>
    );
  } else if (step === STEPS.PROFILE) {
    content = (
      <Box>
        {heading('Before You Begin')}
        {sub("Make sure your baby's name, birth date, photo, and gender are up to date")}
        {/* Reuse the baby-profile edit form; hide its built-in Save (driven by our footer). */}
        <Box
          ref={profileRef}
          mt={3}
          px={[3, 4]}
          sx={{ '& form button[type="submit"]': { display: 'none' } }}
        >
          <EditChildJournal onSuccess={() => setStep(STEPS.SUCCESS)} isFromTft />
        </Box>
      </Box>
    );
  } else {
    content = (
      <Flex flexDirection="column" alignItems="center" textAlign="center">
        {heading('Great! Next, create an invitation to share with your family!')}
        <Image src={giraffe} alt="" width={230} my={4} />
        {sub('After sharing your invitation, watch your Talking Family Tree pages fill up as family members participate.')}
        <Flex mt={4} alignItems="center" px={3} py={1} sx={{ gap: '6px', borderRadius: '100px', bg: FT_COLORS.successBg, color: FT_COLORS.successText }}>
          <FiCheckCircle size={16} />
          <Text fontFamily="Poppins" fontSize="13px" fontWeight={500}>Profile Updated</Text>
        </Flex>
      </Flex>
    );
  }

  const back = () => {
    if (step === STEPS.WELCOME) onClose();
    else setStep((s) => s - 1);
  };

  let primary;
  if (step === STEPS.WELCOME) {
    primary = <PrimaryButton onClick={() => setStep(STEPS.PROFILE)}>Next <FiChevronRight /></PrimaryButton>;
  } else if (step === STEPS.PROFILE) {
    primary = <PrimaryButton onClick={submitProfile}>Next <FiChevronRight /></PrimaryButton>;
  } else {
    primary = (
      <PrimaryButton onClick={() => { onClose(); if (onCreateInvitation) onCreateInvitation(); }}>
        Continue <FiChevronRight />
      </PrimaryButton>
    );
  }

  return (
    <Box
      css="position: fixed; inset: 0; z-index: 10000;"
      sx={{
        bg: isDesktop ? 'rgba(0,0,0,0.5)' : '#fff',
        display: 'flex',
        alignItems: isDesktop ? 'center' : 'stretch',
        justifyContent: 'center',
        // p: isDesktop ? 4 : 0,
        overflowY: 'auto',
      }}
    >
      <Flex
        flexDirection="column"
        bg="white"
        sx={{
          width: '100%',
          maxWidth: isDesktop ? '460px' : '100%',
          minHeight: isDesktop ? 'auto' : '100vh',
          maxHeight: isDesktop ? '92vh' : '100vh',
          borderRadius: isDesktop ? '16px' : 0,
          boxShadow: isDesktop ? 'high' : 'none',
          overflow: 'hidden',
        }}
      >
        <Flex justifyContent="flex-end" p={2}>
          <Box as="button" type="button" onClick={onClose} aria-label="Close"
            sx={{ background: 'none', border: 'none', cursor: 'pointer', color: FT_COLORS.muted, display: 'inline-flex' }}>
            <FiX size={22} />
          </Box>
        </Flex>

        <Box flex="1 1 auto" pb={[9, 3]} css="overflow-y: auto;">
          {content}
        </Box>

        <Flex px={[3, 4]} py={3} alignItems="center" justifyContent={step !== STEPS.WELCOME ? "space-between" : 'flex-end'} sx={{
          borderTop: '1px solid', borderColor: FT_COLORS.rowBorder,
          ...(!isDesktop && {
            position: 'fixed',
            bottom: '0',
            width: '100%',
            left: '0',
            background: 'white',
          })
        }}>
          {step !== STEPS.WELCOME && <Box as="button" type="button" onClick={back}
            sx={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', color: FT_COLORS.text, fontFamily: 'Poppins', fontSize: '14px' }}>
            <FiChevronLeft size={16} /> Back
          </Box>}
          {primary}
        </Flex>
      </Flex>
    </Box>
  );
};

Ftue.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreateInvitation: PropTypes.func,
};

Ftue.defaultProps = {
  onCreateInvitation: null,
};

export default Ftue;
